import {
  PanelFeatureEnum,
  PanelItem,
  PanelItemFieldTypeEnum,
  PanelSection,
  PoiAccessibilityEnum,
  VisibilityEnum,
} from '@/__generated__/graphql';
import { AllPointOfInterestTemplate } from '@/types';
import { GeneralFieldsType } from '../../types';

export type PoiInputKind = 'text' | 'date' | 'enumSelect' | 'status';

export type PoiInputConfig = {
  kind: PoiInputKind;
  enumValues?: readonly string[];
};

export const POI_INPUT_CONFIGS: Partial<Record<keyof GeneralFieldsType, PoiInputConfig>> = {
  name: { kind: 'text' },
  assignee: { kind: 'text' },
  comment: { kind: 'text' },
  workpackNumber: { kind: 'text' },
  visibility: { kind: 'enumSelect', enumValues: Object.values(VisibilityEnum) },
  accessibility: { kind: 'enumSelect', enumValues: Object.values(PoiAccessibilityEnum) },
  dueDate: { kind: 'date' },
  status: { kind: 'status' },
};

export const POI_EDITABLE_KEYS = new Set<string>(Object.keys(POI_INPUT_CONFIGS));

export const BLISTER_EDITABLE_KEYS = new Set<string>([]);

export const EDIT_ONLY_FEATURES = new Set<PanelFeatureEnum>([
  PanelFeatureEnum.MarkAsPit,
  PanelFeatureEnum.ManualSetHeight,
]);

export const isPoiKeyEditable = (key: string): boolean => POI_EDITABLE_KEYS.has(key);

export const isBlisterKeyEditable = (key: string): boolean => BLISTER_EDITABLE_KEYS.has(key);

export const sectionHasFeature = (
  section: PanelSection | undefined,
  feature: PanelFeatureEnum
): boolean => {
  if (!section) return false;
  if (section.feature === feature) return true;
  return (section.groups ?? []).some(
    (group) =>
      group.feature === feature || (group.items ?? []).some((item) => item.feature === feature)
  );
};

export const isItemEditable = (
  item: PanelItem,
  template: AllPointOfInterestTemplate[0] | undefined
): boolean => {
  if (item.feature) return EDIT_ONLY_FEATURES.has(item.feature);

  if (item.fieldType === PanelItemFieldTypeEnum.TemplateField) {
    const templateField = (template?.fields ?? []).find((field) => field.key === item.key);
    if (!templateField) return false;
    return !templateField.isReadOnly;
  }

  if (item.fieldType === PanelItemFieldTypeEnum.Blister) {
    return isBlisterKeyEditable(item.key);
  }

  if (item.fieldType === PanelItemFieldTypeEnum.PointOfInterest) {
    return isPoiKeyEditable(item.key);
  }

  return false;
};
